import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api';

/**
 * TailNumberInput Component
 * An input field with autocomplete functionality for aircraft tail numbers
 *
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Callback when value changes (receives tail number)
 * @param {Function} props.onRemove - Callback when remove button is clicked
 * @param {boolean} props.showRemove - Whether to show the remove button
 * @param {number} props.index - Field index (for numbering)
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.isDuplicate - Whether this tail number is a duplicate
 */
const TailNumberInput = ({
  value,
  onChange,
  onRemove,
  showRemove = false,
  index,
  placeholder = 'Enter tail number (e.g., N12345)',
  isDuplicate = false
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimer = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from API
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setLoading(true);
      const results = await apiClient.searchAircraftByTail(query);

      // Extract tail numbers from results
      const tailNumbers = results
        .filter(aircraft => aircraft.tail_number)
        .map(aircraft => ({
          tailNumber: aircraft.tail_number,
          data: aircraft
        }))
        .slice(0, 10); // Limit to 10 suggestions

      setSuggestions(tailNumbers);
      setShowSuggestions(tailNumbers.length > 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase();
    setInputValue(newValue);
    setSelectedIndex(-1);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);

    // Notify parent of change immediately
    onChange(newValue);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (tailNumber) => {
    setInputValue(tailNumber);
    onChange(tailNumber);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex].tailNumber);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="tail-number-input-wrapper">
      <div className={`tail-number-input-container ${isDuplicate ? 'duplicate' : ''}`}>
        <span className="field-number">{index}</span>
        <div className="input-with-suggestions" ref={inputRef}>
          <input
            type="text"
            className={`tail-number-input ${isDuplicate ? 'duplicate' : ''}`}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
          />
          {loading && <span className="loading-indicator">...</span>}

          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown" ref={suggestionsRef}>
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className={`suggestion-item ${idx === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelectSuggestion(suggestion.tailNumber)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="suggestion-tail">{suggestion.tailNumber}</span>
                  {suggestion.data.operator_name && (
                    <span className="suggestion-operator">{suggestion.data.operator_name}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showRemove && (
          <button
            className="remove-btn"
            onClick={onRemove}
            title="Remove this field"
            aria-label="Remove tail number field"
          >
            ×
          </button>
        )}
      </div>

      {isDuplicate && (
        <div className="duplicate-warning">
          This tail number has already been entered
        </div>
      )}
    </div>
  );
};

export default TailNumberInput;
